import { sentenceCase } from '@chronosphereio/chrono-ui';
import { createUrqlClient, gql } from '@chronosphereio/core';

// Metric Types:
// https://github.com/chronosphereio/monorepo/blob/2a483a4bb0024d87aef52ba17f7e8ddc294f5e70/src/pkg/generated/proto/canonmetricpb/metric.proto#L13C6-L13C16

export type MetricType =
  | 'GAUGE'
  | 'CUMULATIVE_COUNTER'
  | 'DELTA_COUNTER'
  | 'CUMULATIVE_EXPONENTIAL_HISTOGRAM'
  | 'DELTA_EXPONENTIAL_HISTOGRAM'
  | 'MEASUREMENT'
  | 'INVALID';

export type DescriptionSource = 'USER' | 'GENERATED';

type PrometheusMetadataClientArgs = {
  url?: string;
  enableGeneratedDescriptions?: boolean;
};

type GetMetricInfosArgs = {
  includeIsClassicHistogram?: boolean;
  includeSubType?: boolean;
};

export type MetricInfo = {
  name: string;
  type: MetricType | null;
  description: string | null;
  descriptionSource: DescriptionSource | null;

  // Optional fields
  subType?: 'RECORDING_RULE' | 'DERVIED_METRIC';
  expandedQuery?: string;
  isClassicHistogram?: boolean;
};

export const GET_METRIC_METADATAS = gql`
  query GetMetricMetadatas($metricNames: [String!]) {
    metricMetadatas(filter: { metricNames: $metricNames }) {
      metricMetadatas {
        name: metricName
        type: metricType
        descriptionData {
          user
          generated
        }
      }
    }
  }
`;

export const DEAGGREGATE_QUERY = gql`
  query DeaggregateQuery($query: String!) {
    deaggregate(query: $query) {
      layers {
        original
        deaggregated
        replacements {
          original
          replacement
          type
        }
      }
    }
  }
`;

const client = createUrqlClient(fetch);

export class PrometheusMetadataClient {
  url: string;
  enableGeneratedDescriptions: boolean;

  constructor(args: PrometheusMetadataClientArgs) {
    this.url = args.url || '/data/m3';
    this.enableGeneratedDescriptions = args.enableGeneratedDescriptions ?? false;
  }

  async getMetricType(metricName: string): Promise<MetricType | null> {
    const metricInfo = await this.getMetricInfo(metricName, {
      includeIsClassicHistogram: false,
      includeSubType: false,
    });
    return metricInfo?.type ?? null;
  }

  async getIsClassicHistogramMetric(metricName: string): Promise<boolean> {
    const metricInfo = await this.getMetricInfo(metricName);
    return metricInfo?.isClassicHistogram ?? false;
  }

  async getMetricInfo(name: string, args?: GetMetricInfosArgs): Promise<MetricInfo | null> {
    // Since we are only requesting one metric
    const { includeIsClassicHistogram = true, includeSubType = true } = args || {};
    const metrics = await this.getMetricInfos([name], {
      includeIsClassicHistogram,
      includeSubType,
    });
    return metrics[0] ?? null;
  }

  // TODO: Support querying for data to determine if classic histogram.
  // TODO: Support querying for data to determine if derived metric.
  async getMetricInfos(metricNames: string[], args?: GetMetricInfosArgs): Promise<MetricInfo[]> {
    const { includeIsClassicHistogram: includeIsHistogram = false, includeSubType = false } = args || {};
    const metrics = await this.requestMetadatas(metricNames);

    // If we are requesting the isClassicHistogram, we need to check for it
    if (includeIsHistogram) {
      const isClassicHistogram = await Promise.all(metrics.map((m) => this.checkForClassicHistogram(m.name)));
      // Add the isClassicHistogram to the metrics
      metrics.forEach((m, index) => {
        m.isClassicHistogram = isClassicHistogram[index];
      });
    }

    // If we are requesting the subType, we need to check for it
    // This will include things like rollup rules and derived metrics
    if (includeSubType) {
      const metricSubTypeInfo = await Promise.all(metrics.map((m) => this.getMetricSubType(m.name)));
      // Add the subType to the metrics
      metrics.forEach((m, index) => {
        const result = metricSubTypeInfo[index];
        m.subType = result?.subType;
        m.expandedQuery = result?.expandedQuery;
      });
    }

    return metrics;
  }

  private async checkForClassicHistogram(metricName: string) {
    if (metricName.endsWith('_bucket') || metricName.endsWith('_count') || metricName.endsWith('_sum')) {
      const metricNames = getClassicHistogramMetricNames(metricName);
      const metrics = await this.requestMetadatas(metricNames);

      // If we see 3 metrics, then we assume that we have a classic histogram.
      // If we see 2 metrics, then we also assume this, because one of the metrics may be dropped due to a drop rule.
      const isClassicHistogram = metrics.length === 2 || metrics.length === 3;
      return isClassicHistogram;
    }

    return false;
  }

  private async requestMetadatas(metricNames: string[]): Promise<MetricInfo[]> {
    const { data } = await client
      .query(
        GET_METRIC_METADATAS,
        { metricNames },
        {
          requestPolicy: 'cache-first',
        }
      )
      .toPromise();
    return (
      data?.metricMetadatas.metricMetadatas?.map(
        (m: { name: string; type: MetricType; descriptionData: { user: string; generated: DescriptionSource } }) => {
          const d = sanitizeDescriptionData(m.descriptionData, this.enableGeneratedDescriptions);
          return {
            name: m.name,
            type: m.type,
            description: d.description,
            descriptionSource: d.source,
          } as MetricInfo;
        }
      ) ?? []
    );
  }

  private async getMetricSubType(name: string) {
    const resp = await client
      .query(
        DEAGGREGATE_QUERY,
        { query: name },
        {
          requestPolicy: 'cache-first',
        }
      )
      .toPromise();
    const layers = resp.data?.deaggregate?.layers;
    const firstReplacement = layers?.[0]?.replacements?.[0];
    const subType = firstReplacement?.type;
    const expandedQuery = firstReplacement?.replacement;
    return { subType, expandedQuery };
  }
}

/**
 * Takes a description data object and returns a sanitized description data object.
 * @param descriptionData - The description data from the API
 * @param enableGeneratedDescriptions - If true, includes generated descriptions
 */
function sanitizeDescriptionData(
  descriptionData: { user: string; generated: DescriptionSource },
  enableGeneratedDescriptions = false
): {
  source?: DescriptionSource;
  description?: string;
} {
  let source: 'USER' | 'GENERATED' = 'USER';
  let description = undefined;

  if (descriptionData.user) {
    source = 'USER';
    description = descriptionData.user;
  } else if (descriptionData.generated && enableGeneratedDescriptions) {
    source = 'GENERATED';
    description = descriptionData.generated;
  }

  return { source, description };
}

/**
 * When we have a classic histogram, we see 3 metrics with similar names:
 *
 * - metric_X_bucket
 * - metric_X_count
 * - metric_X_sum
 *
 * Given a single metric, we request these 3 metrics from the backend to determine if we have a classic histogram.
 */
function getClassicHistogramMetricNames(metricName: string): string[] {
  const metricPrefix = metricName.replace(/_(bucket|count|sum)$/, '');
  return [`${metricPrefix}_bucket`, `${metricPrefix}_count`, `${metricPrefix}_sum`];
}

/**
 * How to display the metric type in the UI.
 **/
export function displayMetricType(metricType?: MetricType | null): string {
  if (!metricType) return 'Unknown type';
  return sentenceCase(metricType.split('_').join(' '));
}
